import React, { useState, useEffect, useCallback, memo } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  alpha,
  IconButton,
  Tooltip,
} from "@mui/material";
import { History as AuditIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import toast from "react-hot-toast";
import apiService from "../../../services/apiService";
import { crmColors } from "../../../theme/colors";

const formatCurrency = (n) => {
  if (n == null || n === "") return "—";
  const num = Number(n);
  return `${num >= 0 ? "+" : ""}₹${Math.abs(num).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const formatDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString();
};

const actionLabel = (action) =>
  String(action || "")
    .replace(/^wallet_/, "Wallet · ")
    .replace(/^service_/, "Service · ")
    .replace(/_/g, " ");

const sourceColor = (source) => {
  if (source === "wallet") return crmColors.energy.main;
  if (source === "service") return crmColors.primary.main;
  return crmColors.secondary.main;
};

/**
 * Audit log — wallet transactions & service activation events.
 */
const WhatsAppAuditLogs = memo(function WhatsAppAuditLogs({ refreshSignal = 0 }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await apiService.getWhatsAppAdminAuditLog({ limit: 150 });
    if (!result.success) {
      toast.error(result.error || "Failed to load audit log");
      setLoading(false);
      return;
    }
    const data = result.data || {};
    setItems(Array.isArray(data.items) ? data.items : []);
    setTotal(Number(data.total) || 0);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshSignal]);

  return (
    <Stack spacing={2}>
      <Paper elevation={0} sx={{ p: 1.75, borderRadius: 2.5, border: `1px solid ${crmColors.border.default}` }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AuditIcon sx={{ color: crmColors.primary.dark }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={800}>
                Audit log
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Wallet adjustments, recharges & service toggles
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Chip size="small" label={`${total} events`} sx={{ fontWeight: 700 }} />
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={load} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={32} sx={{ color: crmColors.primary.main }} />
        </Box>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: 2.5, border: `1px solid ${crmColors.border.default}`, overflow: "hidden" }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(crmColors.primary.main, 0.05) }}>
                  {["When", "Source", "Action", "School", "Detail", "Amount", "Status"].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.7rem", textTransform: "uppercase" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No audit events recorded yet
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(row.created_at)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.source || "—"}
                          sx={{
                            height: 22,
                            fontWeight: 700,
                            fontSize: "0.68rem",
                            bgcolor: alpha(sourceColor(row.source), 0.1),
                            color: sourceColor(row.source),
                            textTransform: "capitalize",
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ textTransform: "capitalize", fontWeight: 600 }}>
                        {actionLabel(row.action)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={650} noWrap>
                          {row.school_name || row.school_code || "—"}
                        </Typography>
                        {row.school_code && (
                          <Typography variant="caption" color="text.secondary">
                            {row.school_code}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Typography variant="body2" noWrap title={row.detail}>
                          {row.detail || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          color:
                            row.amount == null
                              ? "text.secondary"
                              : Number(row.amount) >= 0
                                ? "success.main"
                                : "error.main",
                        }}
                      >
                        {formatCurrency(row.amount)}
                      </TableCell>
                      <TableCell sx={{ textTransform: "capitalize" }}>{row.status || "—"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Stack>
  );
});

export default WhatsAppAuditLogs;
